import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { audio } from "../lib/audio";

const rand = (a, b) => a + Math.random() * (b - a);

// Arrival: a misty dawn path toward an arch of painted light edged with milky bubbles.
export function Arrival({ simple: simpleIn, rainbow = false, onDone: onDoneIn }) {
  const ref = useRef(null);
  const [out, setOut] = useState(false);
  const [simple] = useState(simpleIn);
  const total = simple ? 4000 : 9000;
  const doneRef = useRef(false);
  const onDone = (why) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDoneIn(why);
  };
  useEffect(() => {
    const id = setTimeout(() => setOut(true), total + 5000);
    return () => clearTimeout(id);
  }, [total]);
  useEffect(() => {
    if (!out) return;
    const id = setTimeout(() => onDone("faded"), 1600);
    return () => clearTimeout(id);
  }, [out]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const c = ref.current;
    const g = c.getContext("2d");
    const dpr = Math.min(1.5, window.devicePixelRatio || 1);
    let W = 0, H = 0, raf;
    const size = () => {
      W = c.clientWidth;
      H = c.clientHeight;
      c.width = W * dpr;
      c.height = H * dpr;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    window.addEventListener("resize", size);
    const bubbles = Array.from({ length: 34 }, (_, i) => ({ a: Math.PI - (i / 33) * Math.PI, side: 0, r: rand(0.035, 0.07), ph: rand(0, 6), pop: 0 }));
    const posts = Array.from({ length: 16 }, (_, i) => ({ y: i / 15, side: i % 2 ? 1 : -1, r: rand(0.03, 0.06), ph: rand(0, 6), pop: 0 }));
    const mists = Array.from({ length: 7 }, (_, i) => ({ z: i / 7, x: rand(-0.4, 0.4) }));
    let popped = false;
    const t0 = performance.now();
    const bubble = (x, y, r, pop) => {
      if (pop >= 1) return;
      const k = 1 + pop * 0.6;
      g.globalAlpha = 1 - pop;
      const gr = g.createRadialGradient(x - r * 0.3, y - r * 0.35, r * 0.1, x, y, r * k);
      gr.addColorStop(0, "rgba(255,255,255,0.95)");
      gr.addColorStop(0.55, "rgba(246,236,250,0.55)");
      gr.addColorStop(1, "rgba(220,200,240,0.15)");
      g.fillStyle = gr;
      g.beginPath();
      g.arc(x, y, r * k, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = "rgba(255,255,255,0.7)";
      g.lineWidth = 1;
      g.stroke();
      g.globalAlpha = 1;
    };
    const frame = (now) => {
      const p = Math.min(1, (now - t0) / total);
      const e = p * p * (3 - 2 * p);
      window.__afaeArrival = { p: +p.toFixed(2), popped, ms: Math.round(now - t0), total };
      const sky = g.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, "#E9DCEB");
      sky.addColorStop(0.5, "#F8E3D3");
      sky.addColorStop(1, "#E6D8DD");
      g.fillStyle = sky;
      g.fillRect(0, 0, W, H);
      const hz = H * 0.52;
      g.fillStyle = "rgba(196,176,196,0.55)";
      g.beginPath();
      g.moveTo(W * 0.47, hz);
      g.lineTo(W * 0.53, hz);
      g.lineTo(W * 0.9, H);
      g.lineTo(W * 0.1, H);
      g.fill();
      const s = simple ? 0.55 + e * 0.35 : 0.18 + e * e * 2.4;
      const cx = W / 2;
      const base = hz + (H - hz) * Math.min(1, s * 0.3);
      const R = Math.min(W, H) * 0.32 * s;
      const glow = g.createRadialGradient(cx, base - R, R * 0.2, cx, base - R * 0.6, R * 1.4);
      glow.addColorStop(0, `rgba(255,236,214,${0.85})`);
      glow.addColorStop(1, "rgba(255,236,214,0)");
      g.fillStyle = glow;
      g.fillRect(0, 0, W, H);
      const inner = g.createRadialGradient(cx, base - R * 0.7, R * 0.1, cx, base - R * 0.7, R * 1.3);
      inner.addColorStop(0, "rgba(255,250,240,0.95)");
      inner.addColorStop(0.6, "rgba(252,226,214,0.75)");
      inner.addColorStop(1, "rgba(226,206,236,0.55)");
      g.fillStyle = inner;
      g.beginPath();
      g.moveTo(cx - R, base);
      g.lineTo(cx - R, base - R);
      g.arc(cx, base - R, R, Math.PI, 0);
      g.lineTo(cx + R, base);
      g.fill();
      g.fillStyle = "#6E4A7E";
      g.textAlign = "center";
      g.font = `${Math.max(14, R * 0.34)}px "Parisienne"`;
      g.fillText("Feeling Heart", cx, base - R * 1.12);
      if (!simple && p > 0.72 && !popped) {
        popped = true;
        audio.pop?.();
      }
      bubbles.forEach((b, i) => {
        if (popped) b.pop = Math.min(1, b.pop + 0.04 + (i % 5) * 0.004);
        const rr = R * 1.06;
        bubble(cx + Math.cos(b.a) * rr, base - R - Math.sin(b.a) * rr + Math.sin(now / 700 + b.ph) * 2, R * b.r * 1.4, b.pop);
      });
      posts.forEach((b) => {
        if (popped) b.pop = Math.min(1, b.pop + 0.045);
        bubble(cx + b.side * R * 1.06, base - R * b.y, R * b.r * 1.4, b.pop);
      });
      mists.forEach((m) => {
        const z = (m.z + e * 1.5) % 1;
        const y = hz + (H - hz) * z;
        const mg = g.createLinearGradient(0, y - 40 * z - 10, 0, y + 40 * z + 10);
        mg.addColorStop(0, "rgba(255,250,252,0)");
        mg.addColorStop(0.5, `rgba(255,250,252,${0.5 * (1 - Math.abs(z - 0.5))})`);
        mg.addColorStop(1, "rgba(255,250,252,0)");
        g.fillStyle = mg;
        g.fillRect(0, y - 50, W, 100);
      });
      if (p >= 1) return setOut(true);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", size);
    };
  }, [simple, total]);
  return (
    <motion.div data-testid="arrival" data-mode={simple ? "simple" : "full"} data-rainbow={rainbow ? "1" : "0"} className="fixed inset-0 z-[60]" animate={{ opacity: out ? 0 : 1 }} transition={{ duration: 0.9 }} onAnimationComplete={() => out && onDone("faded")}>
      <canvas ref={ref} className="h-full w-full" />
      <button data-testid="arrival-skip-button" onClick={() => onDone("skip")} className="absolute bottom-6 right-6 rounded-full border border-white/70 bg-white/60 px-4 py-1.5 font-hand text-lg text-[#4A2D55] backdrop-blur-md transition-colors duration-200 hover:bg-white">
        skip
      </button>
    </motion.div>
  );
}
